import { datadogRum } from "@datadog/browser-rum";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import App from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { Fragment, useEffect } from "react";
import { hotjar } from "react-hotjar";
import { useDispatch, useSelector } from "react-redux";
import Color from "../assets/colors";
import theme from "../assets/theme";
import { HomepageResumeUploadProvider } from "../components/homepage/HomepageResumeUploadContext";
import { ConnorsApplyConfirmProvider } from "../components/shared/ConnorsApplyConfirm/ConnorsApplyConfirmProvider";
import CurrentSalaryDialog from "../components/onboarding/CurrentSalaryDialog/CurrentSalaryDialog";
import * as config from "../config/config";
import { parseJWT } from "../helpers/auth.js";
import { STOP_SYNC_LOGIN } from "../helpers/constant.js";
import { getItem } from "../helpers/data_management";
import i18n from "../helpers/i18n";
import { sendTrackingEvent } from "../helpers/tracking_management";
import {
  updateShowSignInModalStatus,
  updateSignInModalSignUpStatus,
} from "../redux/actions/navbar_action";
import {
  checkAuthentication,
  checkUserSession,
  getUser,
  logout,
  newGetUserInfo,
  triggerSwitchCountry,
} from "../redux/actions/user_action";
import { wrapper } from "../redux/stores/store";
import * as types from "../redux/types/user_type";
import "../styles/global.css";

let cleverTapInitRetryCount = 0;
const SUPPORTED_LANGUAGES = ["en", "ms"];

// outside of react life cycle
if (typeof window !== "undefined") {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host:
      process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
    opt_in_site_apps: false,
    advanced_feature_flags_dedup_per_session: true,
    // opt_in_site_apps: true,
    // Enable debug mode in development
    loaded: (posthogInstance) => {
      window.posthog = posthogInstance;

      if (process.env.NODE_ENV === "development") {
        posthogInstance.debug();
      }
    },
  });
}

function MyApp(props) {
  const { Component, pageProps, err } = props;
  const user = getUser();
  const router = useRouter();
  const dispatch = useDispatch();

  const openSalaryDialog = useSelector(
    (state) => state.dialog.showCurrentSalaryDialog
  );
  const path = router.asPath;

  const isExactJobsOrJobsIn =
    path === "/jobs" ||
    path.startsWith("/jobs-in") ||
    path.startsWith("/internship-jobs") ||
    path.startsWith("/jobs?");

  // Show sign up dialog when specific params is present in URL
  if (router.asPath.includes("signup") && !checkAuthentication()) {
    dispatch(updateShowSignInModalStatus(true));
    dispatch(updateSignInModalSignUpStatus(true));
  }

  useEffect(() => {
    if (typeof window === "undefined") return;

    const cookieLang = Cookies.get("i18next");
    const normalizedCookieLang = cookieLang
      ? cookieLang.toString().toLowerCase().split("-")[0]
      : null;
    const detectedLanguage =
      i18n.resolvedLanguage || i18n.language || SUPPORTED_LANGUAGES[0];
    const normalizedLanguage = detectedLanguage
      .toString()
      .toLowerCase()
      .split("-")[0];
    const nextLanguage = SUPPORTED_LANGUAGES.includes(normalizedCookieLang)
      ? normalizedCookieLang
      : SUPPORTED_LANGUAGES.includes(normalizedLanguage)
      ? normalizedLanguage
      : SUPPORTED_LANGUAGES[0];

    if (i18n.language !== nextLanguage) {
      i18n.changeLanguage(nextLanguage);
    }

    if (Cookies.get("i18next") !== nextLanguage) {
      Cookies.set("i18next", nextLanguage, {
        expires: 365,
        sameSite: "lax",
        path: "/",
      });
    }
  }, []);

  const triggerCleverTapWebPushNotifications = () => {
    try {
      // Grant permission from users to subscribe to CleverTap's web push notifications
      clevertap.notifications.push({
        // apnsWebPushId: "<apple web push id>", // Only for safari browser
        // apnsWebPushServiceUrl: "<safari package service url>", // Only for safari browser
        titleText: "Would you like to receive Push Notifications?",
        bodyText:
          "We promise to only send you relevant content and give you updates on your transactions",
        okButtonText: "Sign me up!",
        rejectButtonText: "No thanks",
        okButtonColor: Color.hiredlyPurple,
        serviceWorkerPath: "./clevertap_sw.js", // path to your custom service worker file
      });
    } catch (error) {
      console.error(error);
    }
  };

  const triggerCleverTap = () => {
    setTimeout(() => {
      try {
        clevertap.profile.push({
          Site: {
            Name: user.name ?? null, // String
            Identity: user.id ?? null, // String or number
            Email: user.email ?? null, // Email address of the user
            Phone: user.mobileNumber ?? null, // Phone (with the country code)
            Gender: user.gender === "male" ? "M" : "F", // Can be either M or F
            DOB: user.birthDate ? new Date(user.birthDate) : null, // Date of Birth. Date object
            SignUpPlatform: user.signupPlatformDeviceOs ?? null, // String
            HaveResume: user.uploadResumeAt ? true : false, // Boolean
            ResumeApproved: user.approved ? true : false, // Boolean
            ResumeApprovalDate: user.approved
              ? user.approve_at
                ? new Date(user.approveAt)
                : null
              : null, // Date object
            LastResumeUploadDate: user.uploadResumeAt
              ? new Date(user.uploadResumeAt)
              : null, // Date object
            RegistrationStartDate: user.createdAt
              ? new Date(user.createdAt)
              : null, // Date object
            // JobTrack:
            //   user.trackIds &&
            //   Array.isArray(user.trackIds) &&
            //   user.trackIds?.length >= 1
            //     ? user.trackIds
            //     : null, // Arrays
            RecentJobTitle: user.current_position ?? null, // String
            RecentCompany: user.current_company ?? null, // String
            ShortSummary: user.short_summary ?? null, // String
          },
        });
      } catch (error) {
        console.error("clevertap_error:", error);

        if (error && cleverTapInitRetryCount < 6) {
          cleverTapInitRetryCount++;
          triggerCleverTap();
        }
      }
    }, 1000);
  };

  function initDatadogFunc() {
    // do not initialise datadog SDK if on local branches.
    if (process.env.NEXT_PUBLIC_DATADOG_ENV === "local") {
      return;
    }

    // CONTINUE IF ENVIRONMENT IS NOT LOCAL
    datadogRum.init({
      applicationId: process?.env?.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
      clientToken: process?.env?.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
      site: "datadoghq.com",
      service: "hiredly-jsw",
      env: process?.env?.NEXT_PUBLIC_DATADOG_ENV,
      // Specify a version number to identify the deployed version of your application in Datadog
      // version: '1.0.0',
      sessionSampleRate: parseInt(
        process?.env?.NEXT_PUBLIC_DATADOG_SESSION_RATE
      ),
      sessionReplaySampleRate: parseInt(
        process?.env?.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_RATE
      ),
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      allowedTracingUrls: [
        // NOTE: Don't use [/https:\/\/.*\.wobbjobs\.com/], it will auto inject Datadog Headers to Wordpress API call
        // as it also ends with "wobbjobs.com" also, which can cause CORS error
        config.baseURL,
        // /https:\/\/.*\.wobbjobs\.com/,
        "https://staging-my.wobbjobs.com",
        "https://api-v2.wobbjobs.com",
        /https:\/\/.*\.hiredly\.com/,
        (url) => url.startsWith(config.baseURL),
        process.env.NEXT_PUBLIC_APP_READONLY_API_URL,
      ],
    });

    // Set user session info for Datadog RUM
    datadogRum.setUser({
      id: user?.id,
      email: user?.email,
      name: user?.name,
    });
  }

  function userInitPosthog(cUser) {
    if (!cUser) return;

    posthog.identify(cUser?.id, {
      uuid: cUser.id,
      email: cUser?.email,
      name: cUser?.name,
      age: parseInt(cUser?.age ?? 0),
      gender: cUser?.gender ?? null,
      nationality: cUser?.nationality ?? null,
      is_actively_available: cUser?.activelyAvailable,
      has_resume: cUser?.resume ? true : false,
      current_location: cUser?.currentLocation,
      is_fresh_grad: cUser?.freshGrad,
      is_onboarding_completed: cUser?.onboardingComplete,
      signup_platform_device_os: cUser?.signupPlatformDeviceOs,
      track_ids: cUser?.trackIds,
      // year_of_experience: workExpYearDiff ?? 0,
    });
  }

  function initCleverTap(cUser) {
    if (!cUser) return;

    if (typeof window !== "undefined") {
      clevertap = require("clevertap-web-sdk");
      clevertap.init(process.env?.NEXT_PUBLIC_CLEVERTAP, "sg1");

      clevertap.onUserLogin.push({
        Site: {
          Name: user?.name ?? null, // String
          Identity: user?.id ?? null, // String or number
          Email: user?.email ?? null,
        },
      });
    }
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      initDatadogFunc();
    }

    if (user?.id && process.env.NEXT_PUBLIC_JSW_GEOLOCATION === "my") {
      if (typeof window !== "undefined") {
        initCleverTap(user);
        // userInitPosthog(user);
      }

      triggerCleverTap();
      // Only trigger CleverTap web push notifications on production
      if (process?.env?.NEXT_PUBLIC_JSW_ENV === "prod") {
        triggerCleverTapWebPushNotifications();
      }
    }
  }, [user]);

  // useEffect(() => {
  //   // HANDLE GLOBAL LOGIN - START
  //   const stopSyncLogin = Cookies.get(STOP_SYNC_LOGIN);
  //   const logoutFlag = Cookies.get(process.env.NEXT_PUBLIC_LOGOUT_FLAG);

  //   async function handleSwitchCountry() {
  //     const cookieGlobalId = Cookies.get(process.env.NEXT_PUBLIC_GLOBAL_ID);

  //     if (cookieGlobalId) {
  //       const doneSwitchCountry = await dispatch(
  //         triggerSwitchCountry({ globalId: cookieGlobalId ?? "" })
  //       );

  //       if (doneSwitchCountry?.success === true) {
  //         dispatch(newGetUserInfo());
  //         triggerCleverTap();

  //         Cookies.set(STOP_SYNC_LOGIN, true);
  //       }
  //     }
  //   }

  //   if (user) {
  //     checkUserSession().then((response) => {
  //       const userToken = getItem(types.USER_TOKEN);

  //       // 🚨 HARD GUARD — token missing
  //       if (!userToken) {
  //         dispatch(logout()).then(() => {
  //           router.push("/");
  //         });
  //         return;
  //       }

  //       const decodedUserToken = parseJWT(userToken);

  //       // 🚨 HARD GUARD — invalid / malformed token
  //       if (
  //         !decodedUserToken ||
  //         !decodedUserToken.expiry_at ||
  //         !decodedUserToken.login_at
  //       ) {
  //         dispatch(logout()).then(() => {
  //           router.push("/");
  //         });
  //         return;
  //       }

  //       const resetPasswordAt = response?.resetPasswordAt
  //         ? dayjs(response.resetPasswordAt)
  //         : null;

  //       const tokenExpiryAt = dayjs(decodedUserToken.expiry_at);
  //       const userLoginAt = dayjs(decodedUserToken.login_at);

  //       if (resetPasswordAt?.isValid()) {
  //         const isTokenExpired = resetPasswordAt.isAfter(tokenExpiryAt);
  //         const isResetAfterLogin = resetPasswordAt.isAfter(userLoginAt);

  //         if (isTokenExpired || isResetAfterLogin) {
  //           dispatch(logout()).then(() => {
  //             router.push("/");
  //           });
  //         }
  //       }
  //     });
  //   }

  //   if (logoutFlag) {
  //     return dispatch(logout());
  //   } else if (!stopSyncLogin || stopSyncLogin !== true) {
  //     // essentially we want to only sync once every
  //     handleSwitchCountry();
  //   }

  //   Cookies.set(STOP_SYNC_LOGIN, true);
  //   // HANDLE GLOBAL LOGIN - END
  // }, []);

  useEffect(() => {
    const stopSyncLogin = Cookies.get(STOP_SYNC_LOGIN) === "true";
    const logoutFlag =
      Cookies.get(process.env.NEXT_PUBLIC_LOGOUT_FLAG) === "true";

    async function handleSwitchCountry() {
      const cookieGlobalId = Cookies.get(process.env.NEXT_PUBLIC_GLOBAL_ID);

      if (!cookieGlobalId) return;

      const done = await dispatch(
        triggerSwitchCountry({ globalId: cookieGlobalId })
      );

      if (done?.success) {
        dispatch(newGetUserInfo());
        triggerCleverTap();
        Cookies.set(STOP_SYNC_LOGIN, "true");
      }
    }

    async function validateSession() {
      if (!user) return;

      // Do not force-redirect to "/" while on reset password page
      if (router.pathname === "/reset-password") {
        return;
      }

      const response = await checkUserSession();
      const userToken = getItem(types.USER_TOKEN);

      if (!userToken) {
        await dispatch(logout());
        router.push("/");
        return;
      }

      const decoded = parseJWT(userToken);

      if (!decoded?.expiry_at || !decoded?.login_at) {
        await dispatch(logout());
        router.push("/");
        return;
      }

      const resetPasswordAt = response?.resetPasswordAt
        ? dayjs(response.resetPasswordAt)
        : null;

      if (resetPasswordAt?.isValid()) {
        if (
          resetPasswordAt.isAfter(dayjs(decoded.expiry_at)) ||
          resetPasswordAt.isAfter(dayjs(decoded.login_at))
        ) {
          await dispatch(logout());
          router.push("/");
        }
      }
    }

    if (logoutFlag) {
      Cookies.remove(process.env.NEXT_PUBLIC_LOGOUT_FLAG);
      dispatch(logout());
      return;
    }

    validateSession();

    if (!stopSyncLogin) {
      handleSwitchCountry();
    }

    Cookies.set(STOP_SYNC_LOGIN, "true");
  }, []);

  useEffect(() => {
    if (user?.id) {
      Cookies.set("userId", user.id, { expires: 365 });
      sendTrackingEvent({
        event: "CE_auto-login",
      });
    }

    // Hotjar initialization code
    hotjar.initialize("2742152", "6");

    // Check if user is logged out and change cookie to false for CleverTap
    Cookies.set("isRegistered", user?.id ? true : false, { expires: 365 });

    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }

    // Check UTM params when page initially loads to store into cookies for ads sign up tracking
    const params = new URL(location.href).searchParams;
    const utm_medium = params.get("utm_medium");
    const URLparams = window?.location.search.substr(1);

    if (utm_medium?.length > 0) {
      Cookies.set("params", URLparams, { expires: 7 });
    } else if (document.cookie.indexOf("params") != -1) {
    }

    const handleRouteChange = () => posthog.capture("$pageview");

    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, []);

  return (
    <Fragment>
      <Head>
        {/* Favicon */}
        <link
          rel="icon"
          href={config.assetDomain + "/images/hiredly/hiredly-favicon.png"}
        />
      </Head>

      <PostHogProvider client={posthog}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* Google Analytics/Tag Manager Script */}
            <noscript>
              <iframe
                src={`https://www.googletagmanager.com/ns.html?id=${process.env.GOOGLE_TAG_MANAGER_ID}`}
                height="0"
                width="0"
                style={{ display: "none", visibility: "hidden" }}
              ></iframe>
            </noscript>

            {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
            <CssBaseline />
            <ConnorsApplyConfirmProvider>
              <HomepageResumeUploadProvider>
                <Component {...pageProps} err={err ? err : {}} />
                {/* Global upload resume floating button */}
              </HomepageResumeUploadProvider>
            </ConnorsApplyConfirmProvider>
            {/* Display current salary dialog */}
            <CurrentSalaryDialog open={openSalaryDialog} />
          </LocalizationProvider>
        </ThemeProvider>
      </PostHogProvider>
    </Fragment>
  );
}

const WrappedApp = wrapper.withRedux(MyApp);

WrappedApp.getInitialProps = async (appContext) => {
  const appProps = await App.getInitialProps(appContext);
  const req = appContext?.ctx?.req;
  if (req) {
    const cookieHeader = req.headers?.cookie || "";
    const match = cookieHeader.match(/(?:^|; )i18next=([^;]+)/);
    const cookieLang = match ? decodeURIComponent(match[1]) : null;
    const normalizedLang = cookieLang
      ? cookieLang.toString().toLowerCase().split("-")[0]
      : null;
    const nextLanguage = SUPPORTED_LANGUAGES.includes(normalizedLang)
      ? normalizedLang
      : SUPPORTED_LANGUAGES[0];

    if (i18n.language !== nextLanguage) {
      await i18n.changeLanguage(nextLanguage);
    }
  }

  return { ...appProps };
};

export default WrappedApp;
